import { apiClient as api } from "./httpClient";

/** vigente | futuro | encerrado — calculado das datas, não armazenado. */
export type TherapyPeriodSituation = "vigente" | "futuro" | "encerrado";

export interface TherapyGoalRow {
  id: string;
  title: string;
  goal: string | null;
  metric: string | null;
  justify: string | null;
  /** Estado atual (0 a 10): de onde o objetivo parte. */
  currentScale: number | null;
  /** Alvo (0 a 10): aonde se quer chegar. */
  targetScale: number | null;
  /** Última nota registrada nas evoluções; null enquanto ninguém avaliou. */
  lastEvaluation: number | null;
  lastEvaluationDate: string | null;
  evaluationCount: number;
  status: string | null;
  priority: string | null;
  /** Sessões previstas para atingir o objetivo. */
  sessionsQuantity: number | null;
  /** Sessões já realizadas apontando este objetivo — com ou sem nota. */
  sessionsDone: number;
  diagnosisLabel: string | null;
  periodId: string | null;
  periodTitle: string | null;
  teamId: string | null;
  teamName: string | null;
  responsibleId: string | null;
  /** Observações do objetivo; texto livre do profissional. */
  notes: string | null;
  /** Conclusão assinada; null enquanto o objetivo segue aberto. */
  conclusion: TherapyGoalConclusion | null;
  professionalNames: string[];
}

/** Parecer de encerramento, com quem assinou (nome + documento profissional). */
export interface TherapyGoalConclusion {
  finalScale: number | null;
  notes: string | null;
  professionalName: string | null;
  professionalDocument: string | null;
  concludedAt: string | null;
}

export interface TherapyPeriodRow {
  id: string;
  title: string;
  description: string | null;
  initialDate: string | null;
  finalDate: string | null;
  situation: TherapyPeriodSituation;
  /** Período da clínica: régua de tempo comum a todos; o profissional escolhe, não edita. */
  isShared: boolean;
  responsibleIds: string[];
  goals: TherapyGoalRow[];
}

/** Coluna do quadro: vem de `patient_plan_stages`, não do código. */
export interface TherapyStageRow {
  id: string;
  name: string;
  color: string | null;
  order: number;
  isFinal: boolean;
  /** Coluna que só aceita objetivo com conclusão assinada. */
  requiresConclusion: boolean;
}

export interface TherapyFilterOption {
  id: string;
  name: string;
  isShared?: boolean;
}

export interface PatientTherapyPlanResponse {
  periods: TherapyPeriodRow[];
  /** Objetivos sem período: existem na base e não podem sumir da tela. */
  goalsWithoutPeriod: TherapyGoalRow[];
  stages: TherapyStageRow[];
  periodOptions: TherapyFilterOption[];
  teamOptions: TherapyFilterOption[];
  responsibleOptions: TherapyFilterOption[];
  /** Todos os ativos: o seletor de administradores do período precisa de quem ainda não
   *  conduz objetivo nenhum deste paciente. */
  professionalOptions: TherapyFilterOption[];
  /** Especialidades da entidade, para o formulário. */
  professionalTypeOptions: TherapyFilterOption[];
  /** Diagnósticos ativos do paciente, para vincular ao objetivo. */
  diagnosisOptions: TherapyFilterOption[];
  priorityOptions: string[];
  statusOptions: string[];
  totalGoals: number;
  pageNumber: number;
  pageSize: number;
}

export interface TherapyPlanFilters {
  periodId?: string;
  status?: string;
  teamId?: string;
  responsibleId?: string;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface SaveTherapyGoalPayload {
  id?: string;
  patientId: string;
  title: string;
  goal: string | null;
  justify: string | null;
  metric: string | null;
  notes: string | null;
  currentScale: number | null;
  targetScale: number | null;
  sessionsQuantity: number | null;
  status: string | null;
  priority: string | null;
  periodId: string | null;
  teamId: string | null;
  responsibleId: string | null;
  professionalTypeId: string | null;
  diagnosisId: string | null;
  initialDate: string | null;
  finalDate: string | null;
}

export interface SaveTherapyPeriodPayload {
  id?: string;
  patientId: string;
  title: string;
  description: string | null;
  initialDate: string | null;
  finalDate: string | null;
  responsibleIds: string[];
}

const MENSAGENS: Record<string, string> = {
  "PatientTherapyPlan.Unauthorized":
    "Você não tem acesso ao plano terapêutico deste paciente.",
  "PatientTherapyPlan.PlanNotFound": "Objetivo não encontrado.",
  "PatientTherapyPlan.StageNotFound": "Fase não encontrada.",
  "PatientTherapyPlan.PeriodNotFound": "Período não encontrado.",
  "PatientTherapyPlan.TitleRequired": "Informe o título do período.",
  "PatientTherapyPlan.InvalidPeriod":
    "Informe as duas datas, e a final não pode ser anterior à inicial.",
  "PatientTherapyPlan.PeriodHasGoals":
    "Este período ainda tem objetivos. Mova-os para outro período antes de excluí-lo.",
  "PatientTherapyPlan.InvalidScale": "A nota final precisa estar entre 0 e 10.",
  "PatientTherapyPlan.ConclusionNotesRequired": "Escreva o parecer da conclusão.",
  "PatientTherapyPlan.ProfessionalRequired":
    "Só um profissional com registro nesta entidade pode assinar a conclusão.",
  "PatientTherapyPlan.NotTheAuthor": "Só quem assinou a conclusão pode reabri-la.",
  "PatientTherapyPlan.GoalConcluded":
    "Objetivo concluído não pode ser alterado. Reabra a conclusão antes.",
  "PatientTherapyPlan.StageRequiresConclusion":
    "Esta coluna só aceita objetivo concluído. Registre a conclusão antes de movê-lo para cá.",
  "PatientTherapyPlan.GoalHasSessions":
    "Este objetivo já tem sessões apontando para ele e não pode ser excluído.",
  "PatientTherapyPlan.BubbleWriteFailed":
    "O sistema de origem recusou a alteração e nada foi gravado. Tente novamente em instantes.",
};

export const therapyPlanErrorMessage = (e: unknown, padrao: string): string => {
  const data = (e as { response?: { data?: { code?: string; description?: string } } })
    ?.response?.data;
  if (data?.code === "PatientTherapyPlan.PeriodHasGoals") {
    return MENSAGENS[data.code];
  }
  return (data?.code && MENSAGENS[data.code]) || padrao;
};

export const patientTherapyPlanService = {
  getByPatient: async (
    patientId: string,
    filters: TherapyPlanFilters = {}
  ): Promise<PatientTherapyPlanResponse> => {
    const response = await api.get(`/patient-therapy-plan/by-patient/${patientId}`, {
      params: {
        periodId: filters.periodId || undefined,
        status: filters.status || undefined,
        teamId: filters.teamId || undefined,
        responsibleId: filters.responsibleId || undefined,
        search: filters.search || undefined,
        pageNumber: filters.pageNumber ?? 1,
        pageSize: filters.pageSize ?? 10,
      },
    });
    return response.data;
  },

  /** Arrasto do quadro: grava no Bubble primeiro; se ele recusar, nada muda. */
  moveGoal: async (goalId: string, stageId: string): Promise<void> => {
    await api.put(`/patient-therapy-plan/goals/${goalId}/stage`, { stageId });
  },

  savePeriod: async (payload: SaveTherapyPeriodPayload): Promise<string> => {
    const response = await api.post(`/patient-therapy-plan/periods`, payload);
    return response.data.id as string;
  },

  deletePeriod: async (id: string): Promise<void> => {
    await api.delete(`/patient-therapy-plan/periods/${id}`);
  },

  saveGoal: async (payload: SaveTherapyGoalPayload): Promise<string> => {
    const response = await api.post(`/patient-therapy-plan/goals`, payload);
    return response.data.id as string;
  },

  deleteGoal: async (goalId: string): Promise<void> => {
    await api.delete(`/patient-therapy-plan/goals/${goalId}`);
  },

  /** Conclui o objetivo: nota final, parecer e a assinatura de quem está na sessão. */
  concludeGoal: async (goalId: string, finalScale: number, finalNotes: string): Promise<void> => {
    await api.put(`/patient-therapy-plan/goals/${goalId}/conclusion`, { finalScale, finalNotes });
  },

  reopenGoal: async (goalId: string): Promise<void> => {
    await api.delete(`/patient-therapy-plan/goals/${goalId}/conclusion`);
  },
};
